import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'

// Map question IDs to full Tableau labels (verified against topline report Q14)
const ACTIVITY_LABELS: Record<string, string> = {
  'Allow_Mining': 'Mining and mineral extraction',
  'Allow_TrophyHunting': 'Guided trophy hunting by outfitters',
  'Allow_AccessZones': 'Zones where human access is limited or prohibited',
  'Allow_LocalHuntFish': 'Hunting and fishing for food by provincial residents',
  'Allow_IndigenousHuntFish': 'Hunting and fishing for food by Indigenous communities in their ancestral territories',
  'Allow_SmallForestry': 'Small-scale, sustainable forestry',
  'Allow_OffroadVehicles': 'Off-road motorized recreation (e.g., ATVs)',
  'Allow_SolarWindFarms': 'Solar and wind energy farms',
  'Allow_ForestryOps': 'Forestry operations',
  'Allow_LargeTourism': 'Large-scale tourism developments (e.g. golf, downhill skiing)',
  'Allow_RecFishing': 'Recreational fishing',
  'Allow_CommercialForestry': 'Large-scale commercial forestry',
  'Allow_MountainBiking': 'Mountain biking',
  'Allow_HydroDams': 'Hydroelectric dams',
}

// Response order: Allowed → Unsure → Strictly limited → Banned
const ALLOW_RESPONSE_ORDER = ['Allowed', 'Unsure', 'Strictly limited', 'Banned']

// Sort responses according to allow order
const sortAllowResponses = (responses: Array<{ label: string; percentage: number }>) => {
  return [...responses].sort((a, b) => {
    const aIdx = ALLOW_RESPONSE_ORDER.indexOf(a.label)
    const bIdx = ALLOW_RESPONSE_ORDER.indexOf(b.label)
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })
}

export function ActivitiesInParks() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  // Get Allow_ questions (activities in protected areas), sorted by support level
  const allowQuestions = data
    ? Object.entries(data.questions)
        .filter(([id]) => id.startsWith('Allow_'))
        .map(([id, question]) => {
          // Get "Allowed" percentage for sorting
          const filterKey = buildFilterKey('total', 'total')
          const questionData = question.data[filterKey]
          const allowedPct = questionData?.responses.find(r => r.label === 'Allowed')?.percentage || 0
          return { id, question, allowedPct }
        })
        .sort((a, b) => b.allowedPct - a.allowedPct) // Sort by highest support first
        .map(({ id, question }) => [id, question] as const)
    : []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          Activities in Protected Areas
        </h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          Break: {breakName}
        </span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          To what extent should your province allow the following to take place in provincial parks and protected areas?
        </p>

        <div className="space-y-6">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading activities questions...</p>
          ) : isBreakActive ? (
            // Break is active - show multiple rows per question
            allowQuestions.map(([questionId, question]) => {
              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey('total', selectedBreak, value, selectedProvince)
                let questionData = question.data[filterKey]
                if (!questionData && selectedProvince) {
                  questionData = question.data[buildFilterKey('total', selectedBreak, value)]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) return null

              const questionLabel = ACTIVITY_LABELS[questionId] || question.shortText || questionId.replace('Allow_', '').replace(/_/g, ' ')

              return (
                <div key={questionId} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{questionLabel}</h3>
                  <div className="space-y-2">
                    {rows.map(row => (
                      <div key={row.filterKey} className="flex items-center gap-2">
                        <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={getDisplayName(row.value)}>
                          {getDisplayName(row.value)}
                        </div>
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={sortAllowResponses(row.data!.responses)}
                            scaleType="allow"
                            height={24}
                            showLabels={false}
                            normalize={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          ) : (
            // Total (no break) - show single bar per question with full labels
            allowQuestions.map(([questionId, question]) => {
              const filterKey = buildFilterKey('total', 'total')
              const questionData = question.data[filterKey]

              if (!questionData) return null

              const questionLabel = ACTIVITY_LABELS[questionId] || question.shortText || questionId.replace('Allow_', '').replace(/_/g, ' ')

              return (
                <div key={questionId} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0">
                  <HorizontalStackedBar
                    segments={sortAllowResponses(questionData.responses)}
                    scaleType="allow"
                    questionText={questionLabel}
                    sampleSize={questionData.sampleSize}
                    height={32}
                  />
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
